<template>
  <section class="tw:flex tw:max-w-[520px] tw:flex-col tw:gap-7" data-testid="reg-step-account">
    <RegistrationStepHeading :title="$t('registration.account.title')" :lede="$t('registration.account.lede')" />

    <!-- a finished account can be recovered with a password reset, an abandoned one only needs signing into -->
    <div
      v-if="conflict"
      class="tw:flex tw:flex-col tw:gap-2 tw:rounded-md tw:border tw:border-orange-200 tw:bg-orange-50 tw:p-4"
      role="alert"
      data-testid="reg-account-conflict">
      <span class="tw:text-13 tw:font-medium tw:text-gray-900">{{ conflict.message }}</span>
      <span class="tw:text-13 tw:text-gray-600">
        <RouterLink :to="{ name: 'login' }" class="tw:font-medium tw:text-primary tw:hover:underline">
          {{ $t('registration.account.sign_in') }}
        </RouterLink>
        <template v-if="conflict.offerReset">
          {{ ' ' }}{{ $t('registration.account.or') }}{{ ' ' }}
          <RouterLink :to="{ name: 'forgotPassword' }" class="tw:font-medium tw:text-primary tw:hover:underline">
            {{ $t('registration.account.reset_password') }}
          </RouterLink>
        </template>
      </span>
    </div>

    <div class="tw:flex tw:flex-col tw:gap-[18px]">
      <LeadInput
        v-model="email"
        type="email"
        autocomplete="email"
        :label="$t('registration.account.email')"
        :errors="fieldErrors.email"
        required
        data-testid="reg-email">
        <template #iconStart><IconMail :size="16" /></template>
        <template #hint>{{ $t('registration.account.email_hint') }}</template>
      </LeadInput>

      <LeadInput
        v-model="mobile"
        type="tel"
        autocomplete="tel"
        :label="$t('registration.account.mobile')"
        :placeholder="$t('registration.account.mobile_placeholder')"
        :errors="fieldErrors.mobile"
        required
        data-testid="reg-mobile">
        <template #iconStart><IconPhone :size="16" /></template>
        <template #hint>{{ $t('registration.account.mobile_hint') }}</template>
      </LeadInput>

      <div class="tw:flex tw:flex-col tw:gap-2">
        <LeadInput
          v-model="password"
          type="password"
          autocomplete="new-password"
          :label="$t('registration.account.password')"
          :errors="fieldErrors.password"
          required
          data-testid="reg-password">
          <template #iconStart><IconLock :size="16" /></template>
        </LeadInput>

        <PasswordStrengthMeter :password="password" />
      </div>

      <!-- split around the links so each fragment stays translatable -->
      <LeadCheckbox v-model="acceptedTerms" data-testid="reg-terms">
        {{ $t('registration.account.terms_before') }}
        <a :href="TERMS_URL" target="_blank" rel="noopener" class="tw:text-primary tw:hover:underline">{{
          $t('registration.account.terms')
        }}</a>
        {{ $t('registration.account.terms_between') }}
        <a :href="PRIVACY_URL" target="_blank" rel="noopener" class="tw:text-primary tw:hover:underline">{{
          $t('registration.account.privacy')
        }}</a
        >{{ $t('registration.account.terms_after') }}
      </LeadCheckbox>

      <!-- honeypot: off-screen rather than display:none because some bots skip hidden inputs -->
      <label class="tw:sr-only" aria-hidden="true">
        <span>Company website</span>
        <input v-model="honeypot" type="text" tabindex="-1" autocomplete="off" data-testid="reg-honeypot" />
      </label>
    </div>
  </section>
</template>

<script setup lang="ts">
/** Step 1 - submitting persists nothing; the server holds the answers and emails a code, so the account is only created in step 2 */
import { IconLock, IconMail, IconPhone } from '@tabler/icons-vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { useToast } from 'vue-toastification';

import { registrationClient } from '@/api/registration';
import LeadCheckbox from '@/components/lead-design/LeadCheckbox.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import PasswordStrengthMeter from '@/components/registration/subcomponents/PasswordStrengthMeter.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { isPasswordAcceptable } from '@/components/registration/subcomponents/passwordStrength';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import {
  readRegistrationErrorBody,
  readRegistrationErrorCode,
  registrationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const toast = useToast();

/** Switzerland only, so the country is fixed */
const MOBILE_COUNTRY = 'CH';
const MIN_MOBILE_LENGTH = 6;
const TERMS_URL = 'https://www.prospective.ch/agb';
const PRIVACY_URL = 'https://www.prospective.ch/datenschutz';

/** never stored (not in the store or session storage); sent once, then the component is destroyed */
const password = ref('');
const acceptedTerms = ref(false);
const honeypot = ref('');
const conflict = ref<{ message: string; offerReset: boolean } | null>(null);
const fieldErrors = reactive<{ email: string; mobile: string; password: string }>({
  email: '',
  mobile: '',
  password: '',
});

/** unrecognised keys (e.g. `accepted_terms`) are dropped rather than crashing */
const FIELD_ERROR_KEYS: Partial<Record<string, keyof typeof fieldErrors>> = {
  email: 'email',
  mobile: 'mobile',
  password: 'password',
};

/* --------------------------------- COMPUTED --------------------------------- */
// bound to the draft so a reload recovers them; the password never joins them
const email = computed({
  get: () => store.draft.account.email,
  set: (value: string) => {
    store.draft.account.email = value;
  },
});

const mobile = computed({
  get: () => store.draft.account.mobile,
  set: (value: string) => {
    store.draft.account.mobile = value;
  },
});

/** gates the button, so it stays quiet while the registrant is still typing */
const isFormValid = computed(
  () =>
    isEmailPlausible(email.value) &&
    mobile.value.trim().length >= MIN_MOBILE_LENGTH &&
    isPasswordAcceptable(password.value) &&
    acceptedTerms.value
);

/* --------------------------------- METHODS ---------------------------------- */
/** catches typos only; rejecting a deliverable address is worse than accepting an undeliverable one */
function isEmailPlausible(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function clearErrors() {
  conflict.value = null;
  fieldErrors.email = '';
  fieldErrors.mobile = '';
  fieldErrors.password = '';
}

/** runs on commit; returns whether it is safe to call the server */
function validateForSubmit() {
  clearErrors();

  if (!isEmailPlausible(email.value)) fieldErrors.email = translator.get('registration.account.email_invalid');
  if (mobile.value.trim().length < MIN_MOBILE_LENGTH)
    fieldErrors.mobile = translator.get('registration.account.mobile_invalid');
  if (!isPasswordAcceptable(password.value))
    fieldErrors.password = translator.get('registration.account.password_invalid');

  return !fieldErrors.email && !fieldErrors.mobile && !fieldErrors.password && acceptedTerms.value;
}

async function submit(): Promise<boolean> {
  if (!validateForSubmit()) return false;

  try {
    const response = await registrationClient.create({
      email: email.value.trim(),
      mobile: mobile.value.replace(/\s+/g, ''),
      mobileCountry: MOBILE_COUNTRY,
      password: password.value,
      acceptedTerms: true,
      honeypot: honeypot.value,
    });

    store.setVerification({
      maskedMobile: response.maskedMobile,
      expiresInSeconds: response.expiresInSeconds,
      resendAfterSeconds: response.resendAfterSeconds,
    });
    // the server echoes the trimmed, lowercased address it sent to
    store.draft.account.email = response.email;

    return true;
  } catch (error) {
    const code = readRegistrationErrorCode(error);

    if (code === 'email_already_registered' || code === 'registration_in_progress') {
      conflict.value = {
        message: translator.get(`registration.account.conflict.${code}`),
        offerReset: code === 'email_already_registered',
      };
      return false;
    }

    // a plain refusal carries none of the codes above and the shared client's toast is off for this call
    const responseJson = readRegistrationErrorBody(error);
    const backendErrors = responseJson?.errors;
    const fieldEntries = Object.entries(backendErrors ?? {}).filter(([, messages]) => messages?.length);

    if (fieldEntries.length) {
      for (const [field, messages] of fieldEntries) {
        const key = FIELD_ERROR_KEYS[field];
        // the server only says the number could not be read; ours names the expected format
        if (key)
          fieldErrors[key] = key === 'mobile' ? translator.get('registration.account.mobile_unreadable') : messages[0];
      }
      return false;
    }

    // no field to pin it to, so show the generic top-level message
    if (responseJson) {
      toast.error(registrationErrorMessage(error, translator.get('common.network_error')));
      return false;
    }

    // anything else is surfaced by the shared client's handler
    throw error;
  }
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.account.onContinue = submit;
  allowNext.value = isFormValid.value;
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});

// editing after a refusal clears it: the message described the old address
watch([email, mobile], () => {
  if (conflict.value) conflict.value = null;
});
</script>
